/**
 * useComponentStatus hook
 *
 * Polls GET /api/v1/components/{id}/status at a configurable interval and
 * returns the latest status without flickering on transient network errors
 * (errors are treated as "unknown", not "not running").
 */

import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@/services/apiClient';
import { isElectronMode, isLocalMode } from '@/utils/apiMode';

export interface ComponentStatus {
  component_id: string;
  is_installed: boolean;
  is_running: boolean;
  version: string | null;
  custom_path: string | null;
}

/**
 * Poll the component status endpoint every intervalMs milliseconds.
 *
 * @param componentId  - The component identifier (e.g. "comfyui", "fooocus")
 * @param intervalMs   - Poll interval in milliseconds (default 10 000)
 * @returns Latest ComponentStatus, or null while the first fetch is pending
 */
export function useComponentStatus(
  componentId: string,
  intervalMs: number = 10_000
): ComponentStatus | null {
  const [status, setStatus] = useState<ComponentStatus | null>(null);
  // Track whether the hook has been unmounted so async callbacks can bail out.
  const cancelledRef = useRef(false);
  // `/api/v1/components/*` lives only in `api_server/local_routes/` (CLAUDE.md RULE #19).
  // In pure cloud/serverless browser mode there is no local backend to poll, so the
  // endpoint always 404s — gate the effect to skip polling entirely.
  const isLocalCapable = isElectronMode() || isLocalMode();

  useEffect(() => {
    if (!isLocalCapable) return;
    cancelledRef.current = false;

    const fetchStatus = async (): Promise<void> => {
      try {
        const res = await apiClient.get<ComponentStatus>(
          `/api/v1/components/${componentId}/status`
        );
        if (!cancelledRef.current) {
          setStatus(res.data);
        }
      } catch {
        // Transient network errors are treated as "unknown" — do not clear
        // the last known good status so the UI doesn't flicker.
        // If the very first fetch fails, status remains null (safe default).
      }
    };

    void fetchStatus();
    const timerId = setInterval(() => {
      void fetchStatus();
    }, intervalMs);

    return () => {
      cancelledRef.current = true;
      clearInterval(timerId);
    };
  }, [componentId, intervalMs, isLocalCapable]);

  return status;
}
